import { Suspense, useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar, { type SidebarRole } from "./Sidebar";
import Chatbot from "./Chatbot";
import BugReportModal from "./BugReportModal";
import Navbar from "./Navbar";
import MeshBackground from "./ui/MeshBackground";
import { LoadingDot } from "./ui";
import { getCurrentUser, onForbidden } from "../services/auth";

const SIDEBAR_COLLAPSED_KEY ="tt:sidebarCollapsed";

const readCollapsed = (): boolean => {
 if (typeof window ==="undefined") return false;
 try {
 return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) ==="1";
 } catch {
 return false;
 }
};

// Map each sidebar role to a visual accent for the bug-report dialog.
const accentForRole = (_role: SidebarRole):"indigo" | "rose" | "emerald"=> {
 return"emerald";
};

const ROLE_CONTEXT_KEY ="tt:roleContext";

const detectRoleFromPath = (pathname: string): SidebarRole | null => {
 // The unified /dashboard route — derive role from the JWT.
 if (pathname ==="/dashboard") return detectRoleFromUser();

 // Unambiguous role-specific URL prefixes always win.

 // Admin-only screens
 if (pathname ==="/admin"|| pathname.startsWith("/admin/")) return"admin";
 if (pathname ==="/createproject") return"admin";

 // /projects is shared across all roles — derive from the actual user role
 // instead of hardcoding to admin.
 if (pathname ==="/projects"|| pathname.startsWith("/projects/")) return detectRoleFromUser();

 // Manager-only screens
 if (pathname ==="/manager"|| pathname.startsWith("/managers/"))
 return"manager";

 // Employee-only screens (just the dashboard — /employees/:id/* is shared
 // because managers also browse those pages)
 if (pathname ==="/employee") return"employee";

 return null;
};

const detectRoleFromUser = (): SidebarRole | null => {
 const u = getCurrentUser();
 if (!u) return null;
 const role = (u.role ||"").trim().toLowerCase();
 if (role ==="admin") return"admin";
 if (role ==="project_manager") return"manager";
 if (role ==="employee") return"employee";
 return null;
};

const recallRoleContext = (): SidebarRole | null => {
 if (typeof window ==="undefined") return null;
 try {
 const r = window.sessionStorage.getItem(ROLE_CONTEXT_KEY);
 if (r ==="admin"|| r ==="manager"|| r ==="employee") return r;
 } catch {
 // ignore
 }
 return null;
};

const rememberRoleContext = (role: SidebarRole) => {
 if (typeof window ==="undefined") return;
 try {
 window.sessionStorage.setItem(ROLE_CONTEXT_KEY, role);
 } catch {
 // ignore
 }
};

const detectRole = (pathname: string): SidebarRole | null => {
 // Priority:
 // 1. URL prefix (e.g. /admin/...) — cache as session context.
 // 2. Session-cached role from the last unambiguous URL visited.
 // 3. Decoded JWT (`getCurrentUser`) — the only durable source of
 // identity now; the old`tt:managerId`/`tt:employeeId`/
 //`tt:user`keys are no longer written.
 const fromPath = detectRoleFromPath(pathname);
 if (fromPath) {
 rememberRoleContext(fromPath);
 return fromPath;
 }
 return recallRoleContext() ?? detectRoleFromUser();
};

const detectUserId = (role: SidebarRole | null): string => {
 if (!role) return"";
 const u = getCurrentUser();
 if (!u) return"";
 // All roles need their own id for self-referencing links (e.g. /employees/:id/worklogs)
 return String(u.id);
};

export default function SidebarLayout() {
 const location = useLocation();
 // Re-evaluate role/id whenever the route changes so dashboard form
 // updates to localStorage immediately reflect in the sidebar.
 const [role, setRole] = useState<SidebarRole | null>(() =>
 detectRole(location.pathname)
 );
 const [userId, setUserId] = useState<string>(() =>
 detectUserId(detectRole(location.pathname))
 );

 const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
 const [mobileOpen, setMobileOpen] = useState(false);
 const [toast, setToast] = useState<string | null>(null);

 useEffect(() => {
 const r = detectRole(location.pathname);
 setRole(r);
 setUserId(detectUserId(r));
 }, [location.pathname]);

 // Close mobile sidebar on route change
 useEffect(() => {
 setMobileOpen(false);
 }, [location.pathname]);

 // Also listen for storage changes (e.g. login completes elsewhere)
 useEffect(() => {
 const handler = () => {
 const r = detectRole(location.pathname);
 setRole(r);
 setUserId(detectUserId(r));
 };
 window.addEventListener("storage", handler);
 return () => window.removeEventListener("storage", handler);
 }, [location.pathname]);

 // Global 403 toast — fed by the axios response interceptor.
 useEffect(() => {
 const off = onForbidden((message) => {
 setToast(message);
 });
 return off;
 }, []);

 useEffect(() => {
 if (!toast) return;
 const timer = window.setTimeout(() => setToast(null), 4500);
 return () => window.clearTimeout(timer);
 }, [toast]);

 if (!role) {
 return <Outlet />;
 }

 const toggleCollapsed = () => {
 setCollapsed((prev) => {
 const next = !prev;
 try {
 if (typeof window !=="undefined") {
 window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ?"1":"0");
 }
 } catch {
 // ignore storage failures (e.g. private mode)
 }
 return next;
 });
 };

 const handleToggleSidebar = () => {
 // On mobile, toggle the mobile drawer
 // On desktop, toggle the collapsed state
 if (window.innerWidth < 1024) {
 setMobileOpen((prev) => !prev);
 } else {
 toggleCollapsed();
 }
 };

 const handleMobileClose = () => {
 setMobileOpen(false);
 };

 // Dynamic content offset class based on sidebar state
 const contentClass = collapsed 
 ?"app-content app-content--sidebar-collapsed":"app-content app-content--sidebar-expanded";

 return (
 <div data-role={role} className="app-layout">
 <Sidebar 
 role={role} 
 userId={userId} 
 collapsed={collapsed}
 mobileOpen={mobileOpen}
 onMobileClose={handleMobileClose}
 />
 <div className={contentClass}>
 <div className="app-main-bg app-main-mesh flex flex-1 flex-col overflow-hidden">
 <MeshBackground />
 <Navbar role={role} onToggleSidebar={handleToggleSidebar} sidebarCollapsed={collapsed} />
 <main className="app-main">
 <Suspense fallback={<LoadingDot />}>
 <Outlet />
 </Suspense>
 </main>
 </div>
 </div>

 <Chatbot role={role} userId={userId} />

 <BugReportModal accent={accentForRole(role)} />

 {toast && (
 <div className="pointer-events-none fixed inset-x-0 top-4 z-[var(--z-toast)] flex justify-center px-4 animate-slide-down">
 <div
 role="alert"className="pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 shadow-lg">
 <svg
 className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-600"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <circle cx="12"cy="12"r="10"/>
 <path d="M12 8v4M12 16h.01"/>
 </svg>
 <div className="flex-1">
 <p className="font-semibold text-rose-900">Access denied</p>
 <p className="mt-0.5 text-rose-800">{toast}</p>
 </div>
 <button
 type="button"onClick={() => setToast(null)}
 className="text-xs font-medium text-rose-700 hover:text-rose-900">
 Dismiss
 </button>
 </div>
 </div>
 )}
 </div>
 );
}
